import { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import _isEqual from 'lodash/isEqual';
import { createSelector, createSelectorCreator, lruMemoize } from 'reselect';
import { v4 as uuidv4 } from 'uuid';

// compares 2 objects deep (obj1.obj and obj2.obj) or shallow (obj1.obj and obj2.obj) based on 'obj.deep' flag
export const flexEqual = <T extends { deep?: boolean; obj: object }>(obj1?: T | null, obj2?: T | null) => {
  let isEqual;

  if (
    obj1 &&
    obj2 &&
    Object.prototype.hasOwnProperty.call(obj1, 'obj') &&
    Object.prototype.hasOwnProperty.call(obj2, 'obj')
  ) {
    if (obj1.deep && obj2.deep) {
      isEqual = JSON.stringify(obj1.obj) === JSON.stringify(obj2.obj); // deep equal
    } else {
      isEqual = obj1.obj === obj2.obj; // shallow equal
    }
  } else {
    isEqual = obj1 === obj2; // standard equal
  }

  return isEqual;
};
// selector creator that supports configurable deep or shallow keys comparison
export const createFlexEqualSelector = createSelectorCreator(lruMemoize, flexEqual);

// arrays shallow compare
export const arraysEqualShallow = <T>(array1?: T[], array2?: T[]) =>
  Boolean(
    (!array1 && !array2) ||
      (array1 &&
        array2 &&
        array1.length === array2.length &&
        array1.every((element, index) => element === array2[index])),
  );

export const createArraysEqualShallowSelector = createSelectorCreator(lruMemoize, arraysEqualShallow);

export function createTrimSelector<T, S, R>(
  selector: (root: S, ...args: any[]) => ImmutableList<T>, // FIXME: in theory this should work with any mappable. find a good type for a mappable data structure so this isn't limited to immutable lists
  trimmer: (val: T) => R,
): (root: S, ...args: any[]) => ImmutableList<R> {
  let dataTrimmedCache: ImmutableList<R>;
  return createSelector(selector, (dataReal) => {
    const dataTrimmedReal = dataReal.map(trimmer);
    if (!_isEqual(dataTrimmedReal, dataTrimmedCache)) {
      dataTrimmedCache = dataTrimmedReal;
    }
    return dataTrimmedCache;
  });
}

export function createValueCache<T extends any[], R>(getValue: (...args: T) => R) {
  let cachedValue: R;
  return (...params: T) => {
    const value = getValue(...params);
    cachedValue = _isEqual(cachedValue, value) ? cachedValue : value;
    return cachedValue;
  };
}

type SimpleObject = { [key: string]: any };
const allItemsEqual = (obj1: SimpleObject, obj2: SimpleObject) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;

  return !keys1.some((key) => obj2[key] !== obj1[key]);
};

let objectStringMap = ImmutableMap<string, ImmutableList<any>>();

type InUseFn = (name: string, cacheString: string) => boolean;
function garbageCollectStringMap(name: string, inUseFn: InUseFn) {
  const newList = objectStringMap.get(name)?.filter((cacheItem) => inUseFn(name, cacheItem.cacheString));

  if (newList) {
    objectStringMap = objectStringMap.set(name, newList);
  }
}

export const getOrCreateStringFromObject = (
  name: string,
  cacheObject: SimpleObject,
  maxSize = 5,
  inUseFn?: InUseFn,
) => {
  const objectStringList = objectStringMap.get(name) || ImmutableList();

  if (inUseFn && objectStringList?.size > maxSize + 1) {
    garbageCollectStringMap(name, inUseFn);
  }

  const cacheItem = objectStringList.find((item) => allItemsEqual(cacheObject, item.cacheObject));
  if (cacheItem) return cacheItem.cacheString;
  const cacheString = uuidv4();
  objectStringMap = objectStringMap.set(name, objectStringList.push({ cacheObject, cacheString }));
  return cacheString;
};

let memoCache = ImmutableMap<string, ImmutableList<any>>();
let keyMap = ImmutableMap();

/*
 *  MEMOIZE WITH KEY SET CACHE
 */
export function memoizeWithKeySetCache(cacheName: string, key: string, value: any, maxSize = 10) {
  let thisCache = memoCache.get(cacheName);

  // if this key already exists, remove it
  if (thisCache) {
    const index = thisCache.findIndex((x) => x.key === key);
    if (index !== -1) thisCache = thisCache.splice(index, 1);
  }

  if (!thisCache) thisCache = ImmutableList([]);
  thisCache = thisCache.push({ key, value });
  if (thisCache.size > maxSize) {
    thisCache = thisCache.slice(1);
  }
  memoCache = memoCache.set(cacheName, thisCache);
}

/*
 * memoizeWithKey Functions
 *
 * Memoize functions where you can control both the key, and the size of the cache
 */

export function memoizeWithKey(cacheName: string, valueFn: () => any, key: string, maxSize = 10) {
  const thisCache = memoCache.get(cacheName);
  if (thisCache) {
    const index = thisCache.findIndex((x) => x.key === key);
    if (index !== -1) return thisCache.get(index).value;
  }

  const ret = valueFn();
  memoizeWithKeySetCache(cacheName, key, ret, maxSize);
  return ret;
}

export function memoizeWithObjectKey(cacheName: string, valueFn: () => any, objectKey: SimpleObject, maxSize?: number) {
  return memoizeWithKey(cacheName, valueFn, getOrCreateStringFromObject(cacheName, objectKey, maxSize));
}

/*
 *  MEMOIZE WITH KEYS (multiple) SET CACHE
 */
//
// This allows you to create a SHARED cache, where multiple cache keys refer to the same value
// This is used to save space/memory when you need to store a single object under multiple keys
//
export function memoizeWithKeysSetCache(cacheName: string, keys: string[], value: any, maxSize?: number) {
  const mainKey = ImmutableList(keys).first();
  if (!mainKey) {
    // eslint-disable-next-line no-console
    console.error('No keys found');
    return;
  }
  memoizeWithKeySetCache(cacheName, mainKey, value, maxSize);

  // Erase all keys associated with mainKey
  const keysToDelete = keyMap.filter((x) => x === mainKey);
  keysToDelete.forEach((_item, key) => {
    keyMap = keyMap.delete(key);
  });

  // Add keys associated with main Key
  keys.slice(1).forEach((associatedKey) => {
    keyMap = keyMap.set(associatedKey, mainKey);
  });
}

/*
 *  MEMOIZE WITH KEY RETRIEVE CACHE
 */
export function memoizeWithKeyRetrieveCache(cacheName: string, key: string) {
  const thisCache = memoCache.get(cacheName);
  if (!thisCache) return undefined;

  let index = thisCache.findIndex((x) => x.key === key);
  if (index !== -1) return thisCache.get(index).value;

  // not found, is this an associated key?
  const associatedKey = keyMap.get(key);
  if (associatedKey) {
    index = thisCache.findIndex((x) => x.key === associatedKey);
    if (index !== -1) return thisCache.get(index).value;
  }
  return null;
}
